/**
 * playbackRate — velocidad de la narración del tutorial (KAN-232).
 *
 * Sólo caché del browser (localStorage) — mismo patrón que tutorialProgress
 * (`mcd:tutorial:{userId}`): por usuario si ya se resolvió el userId, o clave
 * global mientras tanto (la de anon es además el fallback de lectura para
 * usuarios nuevos). Nada viaja al server.
 *
 * Funciones puras + lectura/escritura directa de localStorage: pensadas para
 * usarse desde el TutorialContext (dueño del estado) y testeables con jsdom.
 */

/** Velocidades soportadas por el selector del tutorial. */
export type PlaybackRate = 1 | 1.25 | 1.5 | 2;

export const PLAYBACK_RATES: readonly PlaybackRate[] = [1, 1.25, 1.5, 2];

/** Clave por usuario (mismo esquema que `mcd:changelog_lastSeenId:{userId}`). */
const RATE_KEY = (userId: string) => `mcd:tutorial_playback_rate:${userId}`;
/** Clave global: previa al userId y fallback de lectura. */
const RATE_KEY_ANON = 'mcd:tutorial_playback_rate';

/** Valida que un valor (crudo, ej. de localStorage) sea una velocidad válida. */
export const isPlaybackRate = (v: unknown): v is PlaybackRate =>
  typeof v === 'number' && PLAYBACK_RATES.includes(v as PlaybackRate);

/** Label corto para los chips: 1x · 1,25x · 1,5x · 2x (coma decimal, es-AR). */
export const formatearVelocidad = (rate: PlaybackRate): string =>
  `${rate}x`.replace('.', ',');

/**
 * Lee la velocidad guardada: gana la clave del usuario (si existe y es
 * válida), si no la global. Tolerante a basura/corrupción: cualquier valor
 * inválido o localStorage bloqueado cae al default 1x.
 */
export function leerPlaybackRate(userId?: string): PlaybackRate {
  try {
    if (userId) {
      const scoped = localStorage.getItem(RATE_KEY(userId));
      if (scoped !== null) {
        const num = Number(scoped);
        if (isPlaybackRate(num)) return num;
      }
    }
    const raw = localStorage.getItem(RATE_KEY_ANON);
    if (raw === null) return 1;
    const num = Number(raw);
    return isPlaybackRate(num) ? num : 1;
  } catch {
    return 1;
  }
}

/** Guarda la velocidad: clave del usuario si hay, sino la global. */
export function guardarPlaybackRate(rate: PlaybackRate, userId?: string): void {
  try {
    localStorage.setItem(userId ? RATE_KEY(userId) : RATE_KEY_ANON, String(rate));
  } catch {
    // localStorage lleno/bloqueado: la velocidad vive sólo en memoria.
  }
}
